<template>
    <div>
        <div
            v-if="editing"
            class="container-fluid d-flex shadow py-2"
            style="border: 1px solid var(--primary-color); background: white"
        >
            <form class="form" @submit.prevent="submit">
                <div class="container-fluid">
                    <div class="row">
                        <SbFormGroup class="col-6">
                            <label for="name">Systemnamn</label>
                            <SbFormInput
                                id="name"
                                v-model="name"
                                type="text"
                                required
                                :readonly="isFixed"
                            />
                        </SbFormGroup>
                        <SbFormGroup class="col-6">
                            <label for="label">Fältnamn</label>
                            <SbFormInput
                                id="label"
                                v-model="label"
                                type="text"
                                required
                            />
                        </SbFormGroup>
                        <SbFormGroup class="col-6">
                            <label for="placeholder">Typ</label>
                            <SbFormSelect
                                id="type"
                                v-model="inputType"
                                required
                                :disabled="isFixed"
                            >
                                <SbFormSelectOption
                                    key="-1"
                                    :value="null"
                                    disabled
                                    >Select</SbFormSelectOption
                                >
                                <SbFormSelectOption
                                    v-for="(type, index) in inputTypes"
                                    :key="index"
                                    :value="type.value"
                                    >{{ type.label }}</SbFormSelectOption
                                >
                            </SbFormSelect>
                        </SbFormGroup>
                        <SbFormGroup class="col-6">
                            <label for="placeholder">Visningsnamn</label>
                            <SbFormInput
                                id="placeholder"
                                v-model="placeholder"
                                type="text"
                            />
                        </SbFormGroup>
                        <SbFormGroup class="col-6" v-if="!isFixed">
                            <label for="quote-field-name">QuoteField</label>
                            <SbFormSelect
                                id="quote-field-name"
                                v-model="quoteFieldName"
                                required
                            >
                                <SbFormSelectOption
                                    key="-1"
                                    :value="null"
                                    disabled
                                    >Select</SbFormSelectOption
                                >
                                <SbFormSelectOption
                                    v-for="(field, index) in quoteFields"
                                    :key="index"
                                    :value="field.name"
                                    >{{
                                        `${field.name} [${field.label}]`
                                    }}</SbFormSelectOption
                                >
                            </SbFormSelect>
                        </SbFormGroup>
                    </div>
                </div>
                <SbFormCheckbox v-model="required" switch>
                    Obilgatorisk för kontakten
                    <i
                        v-bind:class="{ 'active-color': required }"
                        class="fal fa-octagon-exclamation"
                    ></i>
                </SbFormCheckbox>
                <hr />

                <div class="d-flex" style="gap: 5px">
                    <button type="submit" class="btn btn-primary btn-sm">
                        Spara
                    </button>
                    <button
                        type="button"
                        class="btn btn-secondary btn-sm"
                        @click="$emit('cancelled')"
                    >
                        Avbryt
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<style scoped lang="scss">
.active-color {
    color: var(--primary-color);
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import SbFormCheckbox from "../sb/SbFormCheckbox.vue";
import SbFormGroup from "../sb/SbFormGroup.vue";
import SbFormInput from "../sb/SbFormInput.vue";
import SbFormSelect from "../sb/SbFormSelect.vue";
import SbFormSelectOption from "../sb/SbFormSelectOption.vue";

interface FieldAttribute {
    name?: string;
    label?: string;
    type?: string | null;
    fieldType?: string;
    enabled?: boolean;
    required?: boolean;
    placeholder?: string;
    quoteFieldName?: string | null;
    [key: string]: unknown;
}

interface Props {
    attribute: FieldAttribute;
    inputTypes?: unknown[];
    quoteFields?: unknown[];
}

const props = defineProps<Props>();
const emit = defineEmits<{ (event: "submitted", payload: FieldAttribute): void }>();
const name = ref<string>();
const label = ref<string>();
const inputType = ref<string | null>();
const fieldType = ref<string>();
const enabled = ref<boolean>();
const required = ref<boolean>();
const placeholder = ref<string>();
const quoteFieldName = ref<string | null>();
const editing = ref(false);
const isFixed = computed(() => fieldType.value === "fixed");

onMounted(() => {
    ({ name: name.value, label: label.value, type: inputType.value, fieldType: fieldType.value,
        enabled: enabled.value, required: required.value, placeholder: placeholder.value,
        quoteFieldName: quoteFieldName.value } = props.attribute);
    editing.value = true;
});

function submit(): void {
    const payload = Object.assign(props.attribute, {
        name: name.value,
        label: label.value,
        type: inputType.value,
        fieldType: fieldType.value,
        enabled: enabled.value,
        required: required.value,
        placeholder: placeholder.value,
        quoteFieldName: quoteFieldName.value,
    });
    emit("submitted", payload);
    editing.value = false;
}
</script>
